import { pflichtAus, type ProjektSteckbrief } from '../../services/projektSteckbrief';

/**
 * Was der A+S-Plan aus der Planungsphase liest — Bedarf und Baubeschreibung.
 *
 * ═══ WOZU ═════════════════════════════════════════════════════════════════
 * Die Planungsphase rechnet den A+S-Verdacht aus zwei Wegen (Entscheid E4:
 * Gebaeudeschadstoffe, Boden/Altlasten) und fuehrt die Baubeschreibung. Der
 * Steckbrief (`services/projektSteckbrief`) reicht beides weiter. Diese Datei
 * macht daraus die Saetze, die im Modulkopf und im Reiter „Grundlagen"
 * stehen — pur, ohne DOM, damit die Worte geprueft sind und nicht erhofft.
 *
 * ═══ EIN AUSFALL IST KEINE ENTWARNUNG ═════════════════════════════════════
 * `pflichtenStand` kennt die Lesefehler des Steckbriefs nicht: ohne Phase
 * sagt es „noch nicht angelegt" — auch dann, wenn die Phase nur nicht GELESEN
 * werden konnte. Deshalb schaut `bedarfsanzeige` zuerst auf
 * `lesefehler.planungsphase` und nennt den Ausfall als solchen. „Nicht
 * erforderlich" steht nur dort, wo die Planungsphase das ausdruecklich
 * ergeben hat.
 *
 * ═══ „NICHT ERFORDERLICH" SPERRT NICHTS ═══════════════════════════════════
 * Die Planungsphase urteilt aus Merkmalen; die Historische Erkundung und die
 * Befunde dieses Moduls wissen mehr. Ein Plan, der trotz „keine" angelegt
 * wird, ist deshalb kein Widerspruch, sondern der Normalfall eines spaeter
 * entdeckten Verdachts. Die Anzeige sagt das dazu.
 *
 * ═══ VORSCHLAG, KEINE VORBELEGUNG ═════════════════════════════════════════
 * `baubeschreibungHinweis` liefert einen Text zum Uebernehmen — gespeichert
 * wird er nur per Klick in der Komponente (Hausregel aller Folgeunterlagen).
 * Stammt der Wert nur aus den Projektdaten (`ungeklaert`), steht der Grund
 * dabei; der Bearbeiter sieht, dass er eine Ersatzquelle uebernimmt.
 */

// ═══════════════════════════════════════════════════════════════════════════
// BEDARFSANZEIGE
// ═══════════════════════════════════════════════════════════════════════════

export type BedarfStand = 'pflicht' | 'ungeklaert' | 'keine' | 'lesefehler';

export interface Bedarfsanzeige {
    stand: BedarfStand;
    /** Kurzsatz fuer den Modulkopf. */
    titel: string;
    /** Begruendung der Planungsphase — nennt bei `ungeklaert`, welche Angabe fehlt. */
    begruendung: string;
    /** Nur Repo-Wortlaut aus `pflichtenStand`; leer, wenn dort keiner steht. */
    rechtsgrundlage: string;
}

export const BEDARF_TITEL: Record<BedarfStand, string> = {
    pflicht: 'Planungsphase: A+S-Plan erforderlich',
    ungeklaert: 'Planungsphase: A+S-Bedarf noch offen',
    keine: 'Laut Planungsphase nicht erforderlich',
    lesefehler: 'Planungsphase nicht lesbar: A+S-Bedarf ungeklärt',
};

/** Steht hinter jedem „nicht erforderlich": das Modul bleibt offen. */
export const BEDARF_KEINE_ZUSATZ =
    'Das Modul bleibt nutzbar: ein Verdacht aus der Historischen Erkundung oder aus Befunden geht vor.';

/** Steht hinter jedem Lesefehler: aus dem Ausfall folgt nichts. */
export const BEDARF_LESEFEHLER_ZUSATZ =
    'Ob der A+S-Plan gebraucht wird, ist damit nicht geklärt. Das ist keine Entwarnung.';

/**
 * Der Hinweis fuer den Modulkopf. `null`, solange der Steckbrief nicht
 * vorliegt — die Komponente zeigt dann „wird gelesen", nicht nichts.
 */
export const bedarfsanzeige = (sb: ProjektSteckbrief | null): Bedarfsanzeige | null => {
    if (!sb) return null;
    const pflicht = pflichtAus(sb, 'asplan');
    const rechtsgrundlage = pflicht?.rechtsgrundlage || '';

    const fehler = sb.lesefehler?.planungsphase;
    if (fehler) {
        return {
            stand: 'lesefehler',
            titel: BEDARF_TITEL.lesefehler,
            begruendung: `Die Planungsphase konnte nicht gelesen werden: ${fehler}. ${BEDARF_LESEFEHLER_ZUSATZ}`,
            rechtsgrundlage,
        };
    }

    if (!pflicht) {
        return {
            stand: 'ungeklaert',
            titel: BEDARF_TITEL.ungeklaert,
            begruendung: 'Die Planungsphase führt den A+S-Bedarf nicht. Das ist keine Entwarnung.',
            rechtsgrundlage,
        };
    }

    if (pflicht.stand === 'pflicht') {
        return { stand: 'pflicht', titel: BEDARF_TITEL.pflicht, begruendung: pflicht.begruendung, rechtsgrundlage };
    }
    if (pflicht.stand === 'keine') {
        return {
            stand: 'keine',
            titel: BEDARF_TITEL.keine,
            begruendung: `${pflicht.begruendung} ${BEDARF_KEINE_ZUSATZ}`.trim(),
            rechtsgrundlage,
        };
    }
    return { stand: 'ungeklaert', titel: BEDARF_TITEL.ungeklaert, begruendung: pflicht.begruendung, rechtsgrundlage };
};

// ═══════════════════════════════════════════════════════════════════════════
// BAUBESCHREIBUNG
// ═══════════════════════════════════════════════════════════════════════════

export type BaubeschreibungHinweis =
    | {
        art: 'vorschlag';
        text: string;
        /** Herkunftstext des Steckbriefs, z. B. „Planungsphase · Grundlagen". */
        quelle: string;
        /** Nur bei einer Ersatzquelle: warum der Wert nicht `belegt` ist. */
        vorbehalt: string | null;
    }
    | {
        art: 'fehlt';
        /** Warum nichts zu uebernehmen ist — der fehlende Wert wird benannt. */
        grund: string;
    };

/**
 * Was der Reiter „Grundlagen" zur Beschreibung des Bauvorhabens anbietet.
 *
 * `null`, wenn das Feld schon gefuellt ist (dann gibt es nichts
 * vorzuschlagen — ueberschrieben wird nie) oder der Steckbrief noch nicht
 * vorliegt. Sonst entweder der Text zum Uebernehmen oder der Grund, warum
 * keiner da ist.
 */
export const baubeschreibungHinweis = (
    sb: ProjektSteckbrief | null,
    aktuell: string | null | undefined,
): BaubeschreibungHinweis | null => {
    if (!sb) return null;
    if (String(aktuell ?? '').trim()) return null;

    const angabe = sb.baubeschreibung;
    const text = typeof angabe?.wert === 'string' ? angabe.wert.trim() : '';
    if (!text) {
        return { art: 'fehlt', grund: angabe?.grund || 'In der Planungsphase ist keine Baubeschreibung erfasst.' };
    }
    return {
        art: 'vorschlag',
        text,
        quelle: angabe.quelle,
        vorbehalt: angabe.stand === 'belegt' ? null : (angabe.grund || 'Herkunft nicht geklärt.'),
    };
};
